import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import DataGrid, { Column, LoadPanel, Paging, SearchPanel, Lookup, Editing, RequiredRule, Button as Butt } from 'devextreme-react/data-grid';
import { TextBox } from 'devextreme-react/text-box';
import CustomStore from 'devextreme/data/custom_store';
import DataSource from 'devextreme/data/data_source';
import { useApiConfigUsuarios } from '../../../api/api.ConfigUsuarios';
import { Profile, User } from '../../../types/types.user';
import { FileUploader } from 'devextreme-react';
import { useAppSelector } from '../../../contexts/hooks';
import { GenToken, locations } from '../../../images/icons';
import { ModalGenerateToken } from './Modals/ModalGenerateToken';
import ModalConfigureLocation from './Modals/ModalConfigureLocation';

const fileUploaderLabel = { 'aria-label': 'Select Photo' };

// Componente para manejar la carga del archivo y conversión a Base64
const FileUploaderCell = ({ data, setValue }: any) => {
  const handleFileChange = (e: any) => {
    const file = e.value[0];
    if (!file) return;

    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      const base64String = reader.result?.toString().split(',')[1];
      setValue(base64String);
    };
    reader.onerror = () => {
      console.error('Error al leer el archivo');
    };
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <FileUploader inputAttr={fileUploaderLabel} selectButtonText='Seleccionar imagen' labelText='' accept='image/*' uploadMode='useForm' multiple={false} onValueChanged={handleFileChange} />
    </div>
  );
};

const Index = () => {
  const dataGridRef = useRef<DataGrid>(null);
  const [data, setData] = useState<User[]>([]);
  const [perfil, setPerfil] = useState<Profile[]>([]);
  const [isTokenModalVisible, setIsTokenModalVisible] = useState(false);
  const [selectedUser, setSelectedUser] = useState<{ id: number; name: string } | null>(null);
  const [isLocationModalVisible, setIsLocationModalVisible] = useState(false);
  const [selectedLocationUserId, setSelectedLocationUserId] = useState<number | null>(null);
  
  // Obtener datos de Redux
  const comboBoxData = useAppSelector((x) => x.configuration.Data?.ComboBox);
  const configGeneral = useAppSelector((x) => x.configuration.Data?.Config_General);
  const Prm_Conf_Usuario_GenToken = useAppSelector((x) => x.userInfo.Permissions?.Prm_Conf_Usuario_GenToken);
  
  // Memoizar selectores para evitar crear nuevos arrays en cada render
  const lstGeneros = useMemo(
    () => comboBoxData?.filter((y) => y.Name === 'SysI_Genero')[0]?.Data,
    [comboBoxData]
  );
  
  const ModuleLocation = useMemo(
    () => JSON.parse(configGeneral?.find((x) => x.Cgn_Nombre === 'ModuleLocation')?.Cgn_Resultado || 'false'),
    [configGeneral]
  );
  
  const { getPerfiles, getUsuarios, setUser, newUser, deleteUser } = useApiConfigUsuarios();

  useEffect(() => {
    getPerfiles().then((res) => {
      setPerfil(res);
    });
  }, []);

  const customDataSrc = useMemo(() => {
    const customData = new CustomStore({
      key: 'Us_Id',
      load() {
        return getUsuarios()
          .then((res) => ({
            data: res,
          }))
          .catch(() => {
            throw new Error('Error de carga de usuarios');
          });
      },
      insert: async (values) => {
        return newUser({ User: { ...values, Us_Firma: values.Us_Firma } })
          .then((res) => {
            if (res === 105) {
              return getUsuarios().then((res) => {
                setData(res);
                return res;
              });
            }
          })
          .catch(() => {
            throw new Error('Error insertando el usuario');
          });
      },
      update: (key, values) => {
        return getUsuarios().then((usuarios) => {
          const rowData = usuarios.find((item) => item.Us_Id === key);
          
          if (rowData) {
            const updatedData = { ...rowData, ...values };
            return setUser({ User: updatedData, IdUser: key })
              .then((res) => {
                if (res === 107) {
                  return getUsuarios().then((res) => {
                    setData(res);
                    return res;
                  });
                }
                return Promise.resolve();
              })
              .catch(() => {
                throw new Error('Error actualizando el usuario');
              });
          }
          return Promise.resolve();
        });
      },
      remove: (key) => {
        return deleteUser({ Id: key }).then((res) => {
          if (res === 107) {
            return getUsuarios().then((res) => {
              setData(res);
              return res;
            });
          }
        });
      },
    });

    return new DataSource({
      store: customData,
      reshapeOnPush: true,
    });
    // Remover las funciones de las dependencias para evitar recrear el store constantemente
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleConfigureClick = useCallback((e) => {
    const userId = e.row.data.Us_Id;
    const userName = `${e.row.data.Us_Nombre} ${e.row.data.Us_Apellido1}`.trim();
    setSelectedUser({ id: userId, name: userName });
    setIsTokenModalVisible(true);
  }, []);

  const handleConfigureLocationClick = useCallback((e) => {
    const userId = e.row.data.Us_Id;
    setSelectedLocationUserId(userId);
    setIsLocationModalVisible(true);
  }, []);

  return (
    <div>
      <DataGrid
        height={'100%'}
        hoverStateEnabled={true}
        dataSource={customDataSrc}
        ref={dataGridRef}
        className='grid custom-datagrid'
        showColumnLines={true}
        rowAlternationEnabled={true}
        repaintChangesOnly={true}
        width={'100%'}
        highlightChanges={true}
        onRowValidating={(e) => {
          const esNuevaFila = !e.oldData;
          const nuevaContraseña = e.newData?.Us_Contrasena;
          if (esNuevaFila && !nuevaContraseña) {
            e.isValid = false;
            e.errorText = 'La contraseña es obligatoria al crear un usuario.';
          }
        }}>
        <LoadPanel enabled={true} />
        <SearchPanel visible placeholder='Buscar' />
        <Paging enabled={false} />
        <Editing mode='popup' allowUpdating={true} allowAdding={true} allowDeleting={true} />

        <Column allowGrouping={false} caption='IDENTIFICACION' visible={false} allowEditing={true} dataField='Us_Identificacion'>
          <RequiredRule />
        </Column>
        <Column allowGrouping={false} caption='NOMBRE' visible={true} allowEditing={true} dataField='Us_Nombre' minWidth={150}>
          <RequiredRule />
        </Column>
        <Column allowGrouping={false} caption='APELLIDO 1' visible={true} allowEditing={true} dataField='Us_Apellido1' minWidth={150}>
          <RequiredRule />
        </Column>
        <Column allowGrouping={false} caption='APELLIDO 2' visible={true} allowEditing={true} dataField='Us_Apellido2' minWidth={150} />
        <Column allowGrouping={false} caption='HOMOLOGACION' visible={false} allowEditing={true} dataField='Us_Homologacion' />
        <Column allowGrouping={false} caption='USUARIO' visible={true} allowEditing={true} dataField='Us_Usuario' width={100}>
          <RequiredRule />
        </Column>
        <Column allowGrouping={false} caption='CONTRASEÑA' visible={false} allowEditing={true} dataField='Us_Contrasena' editCellComponent={({ data }) => <TextBox mode='password' defaultValue={data.value} onValueChanged={(e) => data.setValue(e.value)} />} />
        <Column allowGrouping={false} caption='PERFIL' visible={true} allowEditing={true} dataField='Us_Pe_Id' width={150}>
          <Lookup dataSource={perfil} valueExpr='Pe_Id' displayExpr='Pe_Nombre' />
          <RequiredRule />
        </Column>
        <Column allowGrouping={false} caption='GENERO' visible={false} allowEditing={true} dataField='Us_Gn_Id'>
          <Lookup dataSource={lstGeneros} valueExpr='Gn_Id' displayExpr='Gn_Nombre' />
          <RequiredRule />
        </Column>
        <Column allowGrouping={false} allowSorting={false} caption='ESTADO' allowEditing={true} dataField='Us_Estado' alignment='center' width={90}></Column>
        <Column allowGrouping={false} caption='FECHA EXPIRACION' visible={false} allowEditing={true} dataField='Us_FechaExpira' dataType='datetime' format={'yyyy/MM/dd'}>
          <RequiredRule />
        </Column>
        <Column caption='FIRMA' dataField='Us_Firma' allowEditing={false} visible={false} editCellRender={({ value }) => (value ? <img src={`data:image/jpeg;base64,${value}`} alt='Firma' width='100' height='40' /> : 'No image')} />
        <Column allowGrouping={false} caption='FIRMA' visible={false} allowEditing={true} dataField='Us_Firma' editCellRender={({ data, setValue }) => <FileUploaderCell data={data} setValue={setValue} />}></Column>
        <Column type='buttons' caption={'ACCIÓN'} width={120}>
          <Butt name='edit' />
          <Butt name='delete' />
          {Prm_Conf_Usuario_GenToken && <Butt hint='Generar token' icon={GenToken} onClick={handleConfigureClick} cssClass='custom-token-button' />}
          {ModuleLocation && <Butt hint='Configurar sede' icon={locations} onClick={handleConfigureLocationClick} cssClass='custom-token-button' />}
        </Column>
      </DataGrid>

  {selectedUser && isTokenModalVisible && <ModalGenerateToken isModalVisible={isTokenModalVisible} setIsModalVisible={setIsTokenModalVisible} userId={selectedUser.id} userName={selectedUser.name} />}
  {isLocationModalVisible && selectedLocationUserId && <ModalConfigureLocation isModalVisible={isLocationModalVisible} setIsModalVisible={setIsLocationModalVisible} userId={selectedLocationUserId} />}
    </div>
  );
};

export default Index;
